import { lazy, Suspense } from 'react';
import { createBrowserRouter, redirect, Outlet } from 'react-router-dom';
import { MainLayout } from '@/widgets/layout';
import { authService } from '@/features/auth/model/auth-service';
import { ROUTER_PATHS } from './routes';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/home-page').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('@/pages/login-page').then((m) => ({ default: m.LoginPage })));
const PlantMaintenanceDetailPage = lazy(() =>
	import('@/pages/plant-maintenance-detail-page').then((m) => ({
		default: m.PlantMaintenanceDetailPage,
	}))
);
const ReportsPage = lazy(() => import('@/pages/reports-page').then((m) => ({ default: m.ReportsPage })));
const UserProfilePage = lazy(() => import('@/pages/user-profile-page').then((m) => ({ default: m.UserProfilePage })));
const SettingsPage = lazy(() => import('@/pages/settings-page').then((m) => ({ default: m.SettingsPage })));

/**
 * Protected route loader - redirects to /login if not authenticated
 */
async function protectedLoader() {
	const isAuthenticated = await authService.isAuthenticated();

	if (!isAuthenticated) {
		return redirect(ROUTER_PATHS.login.getLink());
	}
	return null;
}

/**
 * Login page loader - redirects to / if already authenticated
 */
async function loginPageLoader() {
	const isAuthenticated = await authService.isAuthenticated();

	if (isAuthenticated) {
		return redirect(ROUTER_PATHS.root.getLink());
	}
	return null;
}

/**
 * Create router - must be called AFTER auth is initialized
 */
export function createRouter() {
	return createBrowserRouter([
		{
			path: '/',
			element: <Outlet />,
			children: [
				// Protected routes (with MainLayout)
				{
					loader: protectedLoader,
					element: <MainLayout />,
					children: [
						{
							index: true,
							element: (
								<Suspense fallback={null}>
									<HomePage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.plantMaintenanceDetail.path,
							element: (
								<Suspense fallback={null}>
									<PlantMaintenanceDetailPage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.reports.path,
							element: (
								<Suspense fallback={null}>
									<ReportsPage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.userProfile.path,
							element: (
								<Suspense fallback={null}>
									<UserProfilePage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.settings.path,
							element: (
								<Suspense fallback={null}>
									<SettingsPage />
								</Suspense>
							),
						},
					],
				},
				// Public routes
				{
					path: ROUTER_PATHS.login.path,
					loader: loginPageLoader,
					element: (
						<Suspense fallback={null}>
							<LoginPage />
						</Suspense>
					),
				},
			],
		},
		// Catch-all: redirect unknown paths to root
		{
			path: '*',
			loader: () => redirect(ROUTER_PATHS.root.getLink()),
		},
	]);
}
